/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    List,
    ListItem,
    Heading,
    useMultiStyleConfig,
    Divider,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
// import Link from '@salesforce/retail-react-app/app/components/link'
import {pxToRem} from '../../utills/pxToRem'
import {parseCategoryColorConfig} from '../../utills/parseCategoryColorConfig'
import {MENU_DEFAULT_TEXT_COLOR} from '../../constants'
import {ChevronRightIcon} from '../icons'

const LinksList = ({
    links = [],
    isMainContent = false,
    isInnerMenu = false,
    categoryData,
    heading = '',
    hasSubMenu,
    variant,
    color,
    onLinkClick,
    headingLinkRef,
    setfeatureImage,
    setSelectedLevelOneMenu,
    selectedLevelTwoMenu,
    selectedLevelOneMenu,
    setSelectedLevelTwoMenu,
    setLinksForLevelTwoMenu,
    setLinksForLevelThreeMenu,
    setIsLevelTwoMenuOpen,
    setIsLevelThreeMenuOpen,
    setLinksForContentComp,
    setFeatureImageMobileLevelTwo,
    setFeatureImageMobileLevelThree,
    setFeatureImageMobileLevelFour,
    setMenuToggle,
    isMobileMenu,
    parentCategoryName,
    ...otherProps
}) => {
    const styles = useMultiStyleConfig('LinksList', {variant})

    const onLevelOneClick = (id, text) => {
        let selectedLevelTwoMenu = {}
        if (id) {
            selectedLevelTwoMenu = links?.find((item) => item.id === id)
        }

        if (isInnerMenu) {
            setIsLevelThreeMenuOpen(true)
            setFeatureImageMobileLevelFour(heading?.image)
            setLinksForLevelThreeMenu(selectedLevelTwoMenu?.categories)
            setSelectedLevelTwoMenu(heading)
        } else if (isMainContent) {
            setLinksForContentComp(categoryData)
            setFeatureImageMobileLevelTwo(heading.image)
        } else {
            setIsLevelTwoMenuOpen(true)
            setLinksForLevelTwoMenu(categoryData?.categories)
            setSelectedLevelOneMenu(heading)
            setFeatureImageMobileLevelThree(heading?.image)

            setLinksForLevelThreeMenu([])
            setIsLevelThreeMenuOpen(false)

            setSelectedLevelTwoMenu({})
        }

        if (window?.dataLayer) {
            window.dataLayer.push({
                event: 'mainNavClick',
                valueL1: parentCategoryName,
                valueL2: text
            })
        }
    }

    const updateGTMdataLayer = (categoryName) => {
        if (window?.dataLayer) {
            window.dataLayer.push({
                event: 'mainNavClick',
                valueL1: parentCategoryName,
                valueL2: categoryName
            })
        }
    }

    const parsedHeadingColors = parseCategoryColorConfig(heading?.c_categoryTextColor)
    const headingTextColor = parsedHeadingColors?.textColor ?? MENU_DEFAULT_TEXT_COLOR
    const headingHoverColor = parsedHeadingColors?.hoverColor

    return (
        <Box {...styles.container} {...(color ? {color: color} : {})} {...otherProps}>
            {heading &&
                (hasSubMenu ? (
                    <Box
                        as="button"
                        type="button"
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        ref={headingLinkRef}
                        onClick={() => onLevelOneClick(heading.id, heading.text)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onLevelOneClick(heading.id, heading.text)
                            }
                        }}
                        cursor="pointer"
                        backgroundColor="transparent"
                        border="none"
                        padding={0}
                        textAlign="left"
                        _focus={{
                            outline: `${pxToRem(2)} solid ${
                                headingHoverColor || MENU_DEFAULT_TEXT_COLOR
                            }`,
                            outlineOffset: pxToRem(2)
                        }}
                        _focusVisible={{
                            outline: `${pxToRem(2)} solid ${
                                headingHoverColor || MENU_DEFAULT_TEXT_COLOR
                            }`,
                            outlineOffset: pxToRem(2)
                        }}
                    >
                        <Box display="inline-flex" alignItems="center">
                            {heading?.c_categoryIcon &&
                                (heading?.c_categoryIconPosition || 'left') === 'left' && (
                                    <Box
                                        as="span"
                                        marginRight={pxToRem(8)}
                                        display="inline-flex"
                                        alignItems="center"
                                        dangerouslySetInnerHTML={{
                                            __html: heading.c_categoryIcon
                                        }}
                                    />
                                )}
                            <Heading
                                {...styles.heading}
                                fontWeight={600}
                                color={headingTextColor}
                                padding={[0, 0, 0, 0]}
                                paddingRight={0}
                            >
                                {heading.text}
                            </Heading>
                            {heading?.c_categoryIcon &&
                                (heading?.c_categoryIconPosition || 'left') === 'right' && (
                                    <Box
                                        as="span"
                                        marginLeft={pxToRem(8)}
                                        display="inline-flex"
                                        alignItems="center"
                                        dangerouslySetInnerHTML={{
                                            __html: heading.c_categoryIcon
                                        }}
                                    />
                                )}
                        </Box>

                        {hasSubMenu && (
                            <ChevronRightIcon
                                boxSize={pxToRem(14)}
                                color={MENU_DEFAULT_TEXT_COLOR}
                                sx={{
                                    '& use': {
                                        transform: {
                                            base: 'scale(1.7)',
                                            sm: 'scale(1.7)',
                                            md: 'scale(1.5)',
                                            lg: 'scale(1.5)'
                                        },
                                        transformOrigin: 'center',
                                        stroke: 'currentColor',
                                        strokeWidth: '1'
                                    }
                                }}
                            />
                        )}
                    </Box>
                ) : (
                    <Link
                        href={heading?.c_alternativeUrl ? heading?.c_alternativeUrl : heading.href}
                        onClick={() => updateGTMdataLayer(heading.text)}
                        ref={headingLinkRef}
                        {...styles.headingLink}
                        _hover={{
                            textDecoration: 'none',
                            ...(headingHoverColor ? {color: headingHoverColor} : {})
                        }}
                        _focus={{
                            outline: `${pxToRem(2)} solid ${
                                headingHoverColor || MENU_DEFAULT_TEXT_COLOR
                            }`,
                            outlineOffset: pxToRem(2),
                            textDecoration: 'none',
                            ...(headingHoverColor ? {color: headingHoverColor} : {})
                        }}
                        _focusVisible={{
                            outline: `${pxToRem(2)} solid ${
                                headingHoverColor || MENU_DEFAULT_TEXT_COLOR
                            }`,
                            outlineOffset: pxToRem(2),
                            textDecoration: 'none',
                            ...(headingHoverColor ? {color: headingHoverColor} : {})
                        }}
                        paddingTop={{
                            base: pxToRem(0),
                            sm: pxToRem(0),
                            md: pxToRem(0),
                            lg: pxToRem(0)
                        }}
                        paddingBottom={{
                            base: pxToRem(0),
                            sm: pxToRem(0),
                            md: pxToRem(0),
                            lg: pxToRem(0)
                        }}
                        paddingLeft={'0'}
                        paddingRight={{
                            base: pxToRem(0),
                            sm: pxToRem(0),
                            md: pxToRem(0),
                            lg: pxToRem(0)
                        }}
                    >
                        <Heading
                            {...styles.heading}
                            fontSize={[pxToRem(14), pxToRem(14), pxToRem(13), pxToRem(13)]}
                            fontWeight={isMainContent ? '600' : '400'}
                            color={headingTextColor}
                            padding={[0, 0, 0, 0]}
                        >
                            {heading.text}
                        </Heading>

                        {hasSubMenu && (
                            <ChevronRightIcon
                                marginLeft={[pxToRem(50), pxToRem(50), pxToRem(50), pxToRem(50)]}
                            />
                        )}
                    </Link>
                ))}
        </Box>
    )
}

LinksList.propTypes = {
    links: PropTypes.arrayOf(
        PropTypes.shape({
            href: PropTypes.string,
            text: PropTypes.string
        })
    ).isRequired,
    heading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    hasSubMenu: PropTypes.oneOf[(true, false)],
    variant: PropTypes.oneOf(['vertical', 'horizontal']),
    color: PropTypes.string,
    onLinkClick: PropTypes.func,
    headingLinkRef: PropTypes.object,
    setfeatureImage: PropTypes.string,
    setSelectedLevelOneMenu: PropTypes.object,
    selectedLevelTwoMenu: PropTypes.func,
    selectedLevelOneMenu: PropTypes.func,
    setSelectedLevelTwoMenu: PropTypes.func,
    setLinksForLevelTwoMenu: PropTypes.array,
    categoryData: PropTypes.object,
    isInnerMenu: PropTypes.oneOf[(true, false)],
    setLinksForLevelThreeMenu: PropTypes.func,
    setIsLevelTwoMenuOpen: PropTypes.func,
    setIsLevelThreeMenuOpen: PropTypes.func,
    setLinksForContentComp: PropTypes.func,
    parentCategoryName: PropTypes.string,
    setFeatureImageMobileLevelTwo: PropTypes.func,
    setFeatureImageMobileLevelThree: PropTypes.func,
    setFeatureImageMobileLevelFour: PropTypes.func,
    setMenuToggle: PropTypes.func,
    isMobileMenu: PropTypes.oneOf[(true, false)],
    isMainContent: PropTypes.oneOf[(true, false)]
}

export default LinksList
